import apiClient from './client'

export interface DeviceType {
  id: number
  name: string
  code: string
  description?: string
  icon?: string
  color?: string
  sort_order: number
  is_active: boolean
  created_at?: string
  updated_at?: string
}

export const getDeviceTypes = async (): Promise<DeviceType[]> => {
  const response = await apiClient.get('/api/deviceTypes')
  
  // Check if response.data is the array we need (from interceptor)
  if (Array.isArray(response.data)) {
    return response.data
  }
  
  // Fallback for raw response format
  if (response && response.data && response.data.success) {
    return response.data.data
  }
  
  throw new Error('Failed to fetch device types')
}

export const getDeviceType = async (id: number): Promise<DeviceType> => {
  const response = await apiClient.get(`/api/deviceTypes/${id}`)
  
  // API client interceptor already returns response.data.data directly in response.data
  if (response && response.data && typeof response.data === 'object' && 'id' in response.data) {
    return response.data as DeviceType
  }
  
  // Fallback for raw response format
  if (response && response.data && response.data.success) {
    return response.data.data
  }
  
  throw new Error('Device type not found')
}

export const createDeviceType = async (data: Omit<DeviceType, 'id' | 'created_at' | 'updated_at'>): Promise<DeviceType> => {
  const response = await apiClient.post('/api/deviceTypes', data)
  
  // Check if response.data contains the created ID (from interceptor)
  if (response && response.data && typeof response.data === 'object' && 'id' in response.data) {
    return {
      ...data,
      id: parseInt(response.data.id as string),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    } as DeviceType
  }
  
  // API client interceptor already returns response.data.data directly
  if (response && typeof response === 'object' && 'id' in response) {
    return {
      ...data,
      id: parseInt(response.id as string),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    } as DeviceType
  }
  
  // Fallback for raw response format
  if (response && response.data && response.data.success) {
    return {
      ...data,
      id: parseInt(response.data.data.id as string),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    } as DeviceType
  }
  
  throw new Error('Failed to create device type')
}

export const updateDeviceType = async (id: number, data: Partial<DeviceType>): Promise<void> => {
  await apiClient.put(`/api/deviceTypes/${id}`, data)
  
  // API client interceptor handles success response format
  // If no error thrown, operation was successful
}

export const deleteDeviceType = async (id: number): Promise<void> => {
  await apiClient.delete(`/api/deviceTypes/${id}`)
  
  // API client interceptor handles success response format
  // If no error thrown, operation was successful
}
